import {
  getDocs,
  getCountFromServer,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  Timestamp,
  onSnapshot,
} from 'firebase/firestore';
import { getEffectiveTenantId, tenantCollection, tenantPatientCollection } from './tenantContext';
import {
  getConversationId,
  getPatientCareTeamThreadId,
  getCareAppUserForPatientQueries,
  getEffectiveTenantScope,
} from './patientService';
import { getPatientRpmCareManagerIds } from '../utils/patientAccessScope';
import { previousYm, ymInOrgTimeZone } from '../utils/organizationalTime';

async function resolveDashboardTenantScope() {
  const u = await getCareAppUserForPatientQueries();
  return getEffectiveTenantScope(u);
}

/**
 * Get the yyyy-mm key for the Monthly Care Queue month toggle.
 * @param {'month'|'lastMonth'} month
 * @returns {string}
 */
export function getCareQueueYmKey(month) {
  const currentYm = ymInOrgTimeZone();
  return month === 'lastMonth' ? previousYm(currentYm) : currentYm;
}

async function fetchReadingsSinceForPatientIds(patientIds, startDate, perChunkLimit, maxTotal) {
  const tenantId = await getEffectiveTenantId();
  const startTs = Timestamp.fromDate(startDate);
  const ids = [...new Set((patientIds || []).filter(Boolean))];
  if (ids.length === 0) return [];
  const byId = new Map();
  /**
   * Note: We still query the Firestore index on `storedAt` (server receipt) for performance,
   * since `measuredAt` is sparsely populated on legacy data and not always present. The
   * in-memory sort below uses the device-reported time when available so visualizations
   * place batched-uploaded readings in true chronological order.
   */
  const promises = ids.map(async (pid) => {
    const q = query(
      tenantPatientCollection(tenantId, pid, 'readings'),
      where('storedAt', '>=', startTs),
      orderBy('storedAt', 'desc'),
      limit(perChunkLimit)
    );
    const snapshot = await getDocs(q);
    snapshot.docs.forEach((d) => {
      const raw = d.data();
      byId.set(d.id, {
        id: d.id,
        patientId: pid,
        ...raw,
        measuredAt: raw.measuredAt?.toDate?.() ?? raw.measuredAt ?? null,
        storedAt: raw.storedAt?.toDate?.() ?? raw.storedAt,
      });
    });
  });
  await Promise.all(promises);
  const displayMs = (r) => {
    const v = r.measuredAt || r.storedAt || r.receivedAt;
    if (!v) return 0;
    const d = v instanceof Date ? v : new Date(v);
    return Number.isNaN(d.getTime()) ? 0 : d.getTime();
  };
  const list = [...byId.values()].sort((a, b) => displayMs(b) - displayMs(a));
  return list.slice(0, maxTotal);
}

export async function getReadingsLast48HoursForCareUser(role, patientIds) {
  const twoDaysAgo = new Date();
  twoDaysAgo.setDate(twoDaysAgo.getDate() - 2);
  return fetchReadingsSinceForPatientIds(patientIds, twoDaysAgo, 200, 1000);
}

export async function getRecentRpmFlaggedReadingsForCareUser(role, patientIds, daysBack = 90) {
  const start = new Date();
  start.setDate(start.getDate() - daysBack);
  return fetchReadingsSinceForPatientIds(patientIds, start, 200, 2000);
}

/**
 * Exact count of readings with storedAt in [rangeStart, rangeEnd] for the given patient IDs (rules-safe chunks).
 * When impersonating a tenant, scopes by tenantId so Firestore rules accept the query.
 */
export async function getReadingsCountForPatientIdsInRange(patientIds, rangeStart, rangeEnd) {
  const tenantId = await getEffectiveTenantId();
  const ids = [...new Set((patientIds || []).filter(Boolean))];
  if (ids.length === 0) return 0;
  const startTs = Timestamp.fromDate(rangeStart);
  const endTs = Timestamp.fromDate(rangeEnd);
  let total = 0;
  const promises = ids.map(async (pid) => {
    const q = query(
      tenantPatientCollection(tenantId, pid, 'readings'),
      where('storedAt', '>=', startTs),
      where('storedAt', '<=', endTs),
    );
    const agg = await getCountFromServer(q);
    return agg.data().count;
  });
  const counts = await Promise.all(promises);
  for (const c of counts) total += c;
  return total;
}

const MESSAGES_CONVERSATION_IN_CHUNK = 30;
const MESSAGES_PAGE_SIZE = 500;

/**
 * All messages in [rangeStart, rangeEnd] for the given conversation IDs (chunked `in` + pagination). Exact list for aggregation.
 * When impersonating a tenant, scopes by tenantId so Firestore rules accept the query.
 */
export async function fetchMessagesForConversationIdsInRange(conversationIds, rangeStart, rangeEnd) {
  const ids = [...new Set((conversationIds || []).filter(Boolean))];
  if (ids.length === 0) return [];
  const scope = await resolveDashboardTenantScope();
  const tenantId = scope.tenantId;
  const startTs = Timestamp.fromDate(rangeStart);
  const endTs = Timestamp.fromDate(rangeEnd);
  const msgCol = tenantCollection(tenantId, 'messages');
  const all = [];
  for (let i = 0; i < ids.length; i += MESSAGES_CONVERSATION_IN_CHUNK) {
    const chunk = ids.slice(i, i + MESSAGES_CONVERSATION_IN_CHUNK);
    let lastDoc = null;
    while (true) {
      const base = [
        where('conversationId', 'in', chunk),
        where('createdAt', '>=', startTs),
        where('createdAt', '<=', endTs),
        orderBy('createdAt', 'asc'),
        limit(MESSAGES_PAGE_SIZE),
      ];
      const q = lastDoc
        ? query(msgCol, ...base.slice(0, -1), startAfter(lastDoc), limit(MESSAGES_PAGE_SIZE))
        : query(msgCol, ...base);
      const snapshot = await getDocs(q);
      if (snapshot.empty) break;
      snapshot.docs.forEach((d) => {
        all.push({
          id: d.id,
          ...d.data(),
          createdAt: d.data().createdAt?.toDate?.() ?? d.data().createdAt,
          readAt: d.data().readAt?.toDate?.() ?? d.data().readAt,
        });
      });
      lastDoc = snapshot.docs[snapshot.docs.length - 1];
      if (snapshot.docs.length < MESSAGES_PAGE_SIZE) break;
    }
  }
  return all;
}

/**
 * Real-time subscription to messages within a period for a set of conversation IDs.
 * Uses onSnapshot per chunk (Firestore `in` max 30). Merges results and calls `callback`
 * on every change.
 * @returns {Function} unsubscribe
 */
export function subscribeToMessagesInRange(conversationIds, rangeStart, rangeEnd, tenantId, callback) {
  const ids = [...new Set((conversationIds || []).filter(Boolean))];
  if (ids.length === 0 || !tenantId) {
    callback([]);
    return () => {};
  }
  const startTs = Timestamp.fromDate(rangeStart);
  const endTs = Timestamp.fromDate(rangeEnd);
  const msgCol = tenantCollection(tenantId, 'messages');

  const byChunk = new Map();
  const unsubs = [];

  const flush = () => {
    const merged = [];
    byChunk.forEach((msgs) => merged.push(...msgs));
    callback(merged);
  };

  for (let i = 0; i < ids.length; i += MESSAGES_CONVERSATION_IN_CHUNK) {
    const chunk = ids.slice(i, i + MESSAGES_CONVERSATION_IN_CHUNK);
    const chunkKey = i;
    const q = query(
      msgCol,
      where('conversationId', 'in', chunk),
      where('createdAt', '>=', startTs),
      where('createdAt', '<=', endTs),
      orderBy('createdAt', 'asc'),
      limit(MESSAGES_PAGE_SIZE),
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        byChunk.set(chunkKey, snap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
          createdAt: d.data().createdAt?.toDate?.() ?? d.data().createdAt,
          readAt: d.data().readAt?.toDate?.() ?? d.data().readAt,
        })));
        flush();
      },
      (err) => {
        console.warn('Messages subscription error for chunk', i, err);
        byChunk.set(chunkKey, []);
        flush();
      },
    );
    unsubs.push(unsub);
  }
  return () => unsubs.forEach((fn) => fn());
}

export function aggregateRpmAlerts(readings) {
  let unassignedTotal = 0;
  let unassignedLast72h = 0;
  let mismatchTotal = 0;
  let mismatchLast72h = 0;

  const now = new Date();
  const cutoff72 = new Date(now);
  cutoff72.setDate(now.getDate() - 3);

  readings.forEach((r) => {
    const flags = Array.isArray(r.flags) ? r.flags : [];
    const hasUnassigned = flags.includes('unassignedDevice');
    const hasMismatch = flags.includes('parameterMismatch');
    if (!hasUnassigned && !hasMismatch) return;
    const t = r.storedAt;
    const dt = t?.toDate ? t.toDate() : new Date(t);
    if (hasUnassigned) {
      unassignedTotal += 1;
      if (dt >= cutoff72) unassignedLast72h += 1;
    }
    if (hasMismatch) {
      mismatchTotal += 1;
      if (dt >= cutoff72) mismatchLast72h += 1;
    }
  });

  return {
    unassigned: { total: unassignedTotal, last72h: unassignedLast72h },
    mismatch: { total: mismatchTotal, last72h: mismatchLast72h },
  };
}

async function fetchQuarantineReadingsSinceForPatientIds(patientIds, startDate, perChunkLimit, maxTotal) {
  const tenantId = await getEffectiveTenantId();
  const startTs = Timestamp.fromDate(startDate);
  const ids = [...new Set((patientIds || []).filter(Boolean))];
  if (ids.length === 0) return [];
  const byId = new Map();
  const promises = ids.map(async (pid) => {
    const q = query(
      tenantPatientCollection(tenantId, pid, 'readings'),
      where('submissionStatus', '==', 'quarantine'),
      where('storedAt', '>=', startTs),
      orderBy('storedAt', 'desc'),
      limit(perChunkLimit),
    );
    const snapshot = await getDocs(q);
    snapshot.docs.forEach((d) => {
      byId.set(d.id, {
        id: d.id,
        patientId: pid,
        ...d.data(),
        storedAt: d.data().storedAt?.toDate?.() ?? d.data().storedAt,
      });
    });
  });
  await Promise.all(promises);
  const list = [...byId.values()].sort((a, b) => {
    const ta = a.storedAt instanceof Date ? a.storedAt.getTime() : new Date(a.storedAt).getTime();
    const tb = b.storedAt instanceof Date ? b.storedAt.getTime() : new Date(b.storedAt).getTime();
    return tb - ta;
  });
  return list.slice(0, maxTotal);
}

export async function getRecentQuarantineReadingsForCareUser(role, patientIds, daysBack = 90) {
  const start = new Date();
  start.setDate(start.getDate() - daysBack);
  return fetchQuarantineReadingsSinceForPatientIds(patientIds, start, 200, 2000);
}

export function aggregateQuarantineAlerts(readings) {
  let total = 0;
  let last72h = 0;
  const now = new Date();
  const cutoff72 = new Date(now);
  cutoff72.setDate(now.getDate() - 3);
  readings.forEach((r) => {
    if (r.submissionStatus !== 'quarantine') return;
    total += 1;
    const t = r.storedAt;
    const dt = t?.toDate ? t.toDate() : new Date(t);
    if (dt >= cutoff72) last72h += 1;
  });
  return { total, last72h };
}

/**
 * Count readings in memory that fall within [todayStart, todayEnd] for the given patient IDs.
 */
export function countReadingsToday(readings, patientIds, todayStart, todayEnd) {
  const set = new Set(patientIds);
  return readings.filter((r) => {
    if (!set.has(r.patientId)) return false;
    const t = r.storedAt;
    const date = t?.toDate ? t.toDate() : new Date(t);
    return date >= todayStart && date <= todayEnd;
  }).length;
}

/**
 * Build set of message conversationIds for dashboard scope: shared care-team thread per patient
 * plus legacy sorted(patientId, cmUid) ids so older messages still count.
 */
export function getConversationIdsForPatients(patients) {
  const set = new Set();
  patients.forEach((p) => {
    if (!p?.id) return;
    const team = getPatientCareTeamThreadId(p.id);
    if (team) set.add(team);
    const cmIds = getPatientRpmCareManagerIds(p);
    cmIds.forEach((cmId) => {
      const legacy = getConversationId(p.id, cmId);
      if (legacy) set.add(legacy);
    });
  });
  return set;
}

/** Map each conversation id for these patients to that patient’s id (for resolving legacy rows without `patientId`). */
export function getConversationIdToPatientIdMap(patients) {
  const map = new Map();
  (patients || []).forEach((p) => {
    if (!p?.id) return;
    const team = getPatientCareTeamThreadId(p.id);
    if (team) map.set(team, p.id);
    const cmIds = getPatientRpmCareManagerIds(p);
    cmIds.forEach((cmId) => {
      const legacy = getConversationId(p.id, cmId);
      if (legacy) map.set(legacy, p.id);
    });
  });
  return map;
}

/**
 * Oldest-first unread messages from patients in the period window (createdAt already constrained by fetch; bounds optional extra guard).
 * @returns {{ id: string, patientId: string, createdAt: Date, body: string }[]}
 */
export function selectOldestUnreadPatientMessagesForDashboard(
  messages,
  conversationIds,
  conversationIdToPatientId,
  periodStart,
  periodEnd,
  maxRows
) {
  const cap = Math.max(0, Number(maxRows) || 0);
  if (!messages?.length || cap === 0) return [];

  const startMs = periodStart?.getTime?.() ?? 0;
  const endMs = periodEnd?.getTime?.() ?? 0;

  const enriched = [];
  messages.forEach((m) => {
    if (!conversationIds.has(m.conversationId)) return;
    if (m.senderRole !== 'patient') return;
    if (m.readAt != null) return;
    const created = m.createdAt?.toDate ? m.createdAt.toDate() : new Date(m.createdAt);
    if (created.getTime() < startMs || created.getTime() > endMs) return;
    let patientId = typeof m.patientId === 'string' && m.patientId ? m.patientId : null;
    if (!patientId) patientId = conversationIdToPatientId.get(m.conversationId) || null;
    if (!patientId) return;
    const rawBody = m.body;
    const body =
      rawBody == null ? '' : typeof rawBody === 'string' ? rawBody : String(rawBody);
    enriched.push({ id: m.id, patientId, createdAt: created, body });
  });

  enriched.sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());
  return enriched.slice(0, cap);
}

/**
 * Aggregate message counts: from patients (read/unread), from care managers (read/unread).
 * messages: array of message docs with senderRole, readAt, conversationId.
 * conversationIds: Set of conversation IDs that belong to the dashboard scope.
 */
export function aggregateMessageCounts(messages, conversationIds) {
  const inScope = (m) => conversationIds.has(m.conversationId);
  const fromPatient = (m) => m.senderRole === 'patient';
  const fromCareManager = (m) => m.senderRole === 'care manager';
  const read = (m) => m.readAt != null;

  let patientRead = 0;
  let patientUnread = 0;
  let careManagerRead = 0;
  let careManagerUnread = 0;

  messages.forEach((m) => {
    if (!inScope(m)) return;
    if (fromPatient(m)) {
      if (read(m)) patientRead += 1;
      else patientUnread += 1;
    } else if (fromCareManager(m)) {
      if (read(m)) careManagerRead += 1;
      else careManagerUnread += 1;
    }
  });

  return {
    fromPatients: { read: patientRead, unread: patientUnread, total: patientRead + patientUnread },
    fromCareManagers: { read: careManagerRead, unread: careManagerUnread, total: careManagerRead + careManagerUnread },
  };
}

/**
 * Pathway assignments for the given patients only (one subcollection read per patient).
 */
export async function getPathwayAssignmentsForPatientIds(patientIds) {
  const tenantId = await getEffectiveTenantId();
  const ids = [...new Set((patientIds || []).filter(Boolean))];
  if (ids.length === 0) return [];
  const rows = [];
  const promises = ids.map(async (pid) => {
    const snapshot = await getDocs(tenantPatientCollection(tenantId, pid, 'pathwayAssignments'));
    snapshot.docs.forEach((d) => {
      rows.push({
        id: d.id,
        patientId: pid,
        ...d.data(),
        completedByPatient: d.data().completedByPatient === true,
        active: d.data().active !== false,
      });
    });
  });
  await Promise.all(promises);
  return rows;
}

/**
 * Real-time subscription to rpmDeviceHelpQueue for a tenant.
 * Callback receives normalized rows on every change.
 * @returns {Function} unsubscribe
 */
export function subscribeOpenPairingHelpQueue(tenantId, callback) {
  if (!tenantId) { callback([]); return () => {}; }
  return onSnapshot(
    tenantCollection(tenantId, 'rpmDeviceHelpQueue'),
    (snap) => {
      const rows = snap.docs.map((d) => ({
        id: d.id,
        ...d.data(),
        lastAttemptAt: d.data().lastAttemptAt?.toDate?.() ?? d.data().lastAttemptAt,
        firstAttemptAt: d.data().firstAttemptAt?.toDate?.() ?? d.data().firstAttemptAt,
      }));
      callback(rows);
    },
    (err) => {
      console.warn('Pairing help queue subscription error', err);
      callback([]);
    },
  );
}

/**
 * Real-time subscription to readingSyncIssues for a tenant (patient sync-health
 * telemetry). Callback receives normalized rows on every change.
 * @returns {Function} unsubscribe
 */
export function subscribeReadingSyncIssues(tenantId, callback) {
  if (!tenantId) { callback([]); return () => {}; }
  return onSnapshot(
    tenantCollection(tenantId, 'readingSyncIssues'),
    (snap) => {
      const rows = snap.docs.map((d) => ({
        id: d.id,
        ...d.data(),
        lastAttemptAt: d.data().lastAttemptAt?.toDate?.() ?? d.data().lastAttemptAt,
        firstStuckAt: d.data().firstStuckAt?.toDate?.() ?? d.data().firstStuckAt,
      }));
      callback(rows);
    },
    (err) => {
      console.warn('Reading sync issues subscription error', err);
      callback([]);
    },
  );
}

/**
 * One-time fetch of readingSyncIssues rows for the effective tenant (used by the
 * "Unsynced readings" list page). Normalizes timestamps to Date.
 * @returns {Promise<Array>}
 */
export async function getReadingSyncIssueItems() {
  const tenantId = await getEffectiveTenantId();
  if (!tenantId) return [];
  const snap = await getDocs(tenantCollection(tenantId, 'readingSyncIssues'));
  return snap.docs.map((d) => ({
    id: d.id,
    ...d.data(),
    lastAttemptAt: d.data().lastAttemptAt?.toDate?.() ?? d.data().lastAttemptAt,
    firstStuckAt: d.data().firstStuckAt?.toDate?.() ?? d.data().firstStuckAt,
  }));
}

/**
 * Aggregate unsynced-reading markers scoped to a set of patient IDs. A patient counts
 * as "stuck" once their pending readings have been unsynced for more than `stuckHours`
 * (default 24h), measured from `firstStuckAt`.
 * @returns {{ total: number, stuck: number, rows: Array }}
 */
export function aggregateUnsyncedReadings(rows, allowedPatientIds, stuckHours = 24) {
  const allowed = new Set(allowedPatientIds);
  const now = Date.now();
  const stuckCutoff = now - stuckHours * 60 * 60 * 1000;

  let total = 0;
  let stuck = 0;
  const affected = [];

  rows.forEach((r) => {
    const pending = Number(r.pendingCount) || 0;
    if (pending <= 0) return;
    if (!r.patientId || !allowed.has(r.patientId)) return;
    total += 1;
    const t = r.firstStuckAt;
    const dt = t instanceof Date ? t : (t ? new Date(t) : null);
    const isStuck = dt ? dt.getTime() <= stuckCutoff : false;
    if (isStuck) {
      stuck += 1;
      affected.push(r);
    }
  });

  return { total, stuck, rows: affected };
}

/**
 * Aggregate open pairing-help rows scoped to a set of patient IDs.
 * @returns {{ total: number, last72h: number, hasNullPatientId: boolean }}
 */
export function aggregatePairingHelpOpen(rows, allowedPatientIds) {
  const allowed = new Set(allowedPatientIds);
  const now = new Date();
  const cutoff72 = new Date(now);
  cutoff72.setDate(now.getDate() - 3);

  let total = 0;
  let last72h = 0;
  let hasNullPatientId = false;

  rows.forEach((r) => {
    const st = r.status || 'open';
    if (st !== 'open') return;
    if (!r.patientId) { hasNullPatientId = true; return; }
    if (!allowed.has(r.patientId)) return;
    total += 1;
    const t = r.lastAttemptAt;
    const dt = t instanceof Date ? t : new Date(t);
    if (dt >= cutoff72) last72h += 1;
  });

  return { total, last72h, hasNullPatientId };
}

/**
 * Count pathway assignments: completed (completedByPatient === true) and incomplete (active and not completed).
 */
export function aggregatePathwayCounts(assignments, patientIds) {
  const set = new Set(patientIds);
  let completed = 0;
  let incomplete = 0;
  assignments.forEach((a) => {
    if (!set.has(a.patientId)) return;
    if (a.completedByPatient) completed += 1;
    else if (a.active) incomplete += 1;
  });
  return { completed, incomplete };
}
